import {
  Button as NextUIButton,
  type ButtonProps as NextUIButtonProps,
} from '@heroui/button';
import { forwardRef } from 'react';
import Tooltip, { type TooltipProps } from './tooltip';

export interface ButtonProps extends NextUIButtonProps {
  tooltip?: TooltipProps;
  kind?: 'primary' | 'secondary' | 'tertiary' | 'icon-only' | 'custom' | 'dark-icon' ;
}

const Button = forwardRef(
  ({ size = 'md', kind = 'primary', isIconOnly = false, tooltip, ...rest }: ButtonProps, ref: any) => {
    if (kind === 'primary') {
      rest.className = `bg-primary-500 hover:bg-primary-600 font-semibold text-white  aria-expanded:bg-primary-600 ${rest.className} `;
    }

    if (kind === 'custom') {
      rest.className = `${rest.className}`;
    }

    if (kind === 'secondary') {
      rest.className = `bg-grey-100 hover:bg-grey-200 dark:bg-grey-800 hover:dark:bg-grey-700 font-semibold text-grey-900 dark:text-white dark:border dark:border-grey-700/30 ${rest.className} aria-expanded:bg-grey-200`;
    }

    if (kind === 'tertiary') {
      rest.color = 'default';
      rest.variant = 'bordered';
      rest.className = `bg-white dark:bg-transparent border border-grey-200 dark:border-grey-700 hover:bg-grey-200 hover:dark:bg-grey-700 font-semibold text-grey-900 dark:text-grey-200 ${rest.className} aria-expanded:bg-grey-200`;
    }

    if (kind === 'icon-only') {
      rest.color = 'default';
      rest.variant = 'bordered';
      rest.className = `min-w-8 w-8 px-0 border hover:bg-grey-200 hover:dark:bg-grey-800 ${rest.className} aria-expanded:bg-grey-200 aria-expanded:dark:bg-grey-800`;
    }

    if (kind === 'dark-icon') {
      rest.className = `min-w-8 w-8 px-0 bg-grey-900/10 backdrop-blur-lg text-grey-600 hover:text-white hover:bg-grey-900/80 font-semibold ${rest.className} aria-expanded:bg-grey-900/10`;
    }

    if (isIconOnly) {
      rest.className = `min-w-[38px] w-[38px] px-0 ${rest.className}`;
    }

    if (rest.isDisabled) {
      rest.className = `opacity-50 cursor-not-allowed ${rest.className}`;
    }

    const sizes = {
      sm: 'h-8',
      md: 'h-9.5',
      lg: 'h-11',
    }[size];

    const component = (
      <NextUIButton
        hover
        disableRipple
        isIconOnly={isIconOnly}
        ref={ref}
        {...rest}
        isDisabled={rest.isDisabled || rest.disabled}
        className={`rounded-md gap-2 antialiased ${sizes} px-4 py-0 ${rest.className} aria-expanded:opacity-100 aria-expanded:scale-100`}
      />
    );

    return tooltip ? <Tooltip {...tooltip}>{component}</Tooltip> : component;
  }
);

export default Button;
